import { axiosInstance } from '../clients/axiosClient';

export interface CreateResourceRequest {
  name: string;
  content: string;
  biomarker_ids: number[];
  condition_ids: number[];
  body_system_ids: number[];
  mri_condition_ids: number[];
  image?: string;
  video_url?: string;
}

export interface CreateResourceResponse {
  id: number;
  status: string;
  message: string;
}

export interface UpdateResourceRequest {
  resource_id: number;
  name?: string;
  content?: string;
  biomarker_ids?: number[];
  condition_ids?: number[];
  body_system_ids?: number[];
  mri_condition_ids?: number[];
  image?: string;
  video_url?: string;
}

export interface UpdateResourceResponse {
  id: number;
  status: string;
}

export interface DeleteResourceRequest {
  resource_id: number;
}

export interface DeleteResourceResponse {
  id: number;
  status: string;
  message: string;
}

export interface Biomarker {
  id: number;
  name: string;
}

export interface Condition {
  id: number;
  name: string;
}

export interface ResourceBodySystem {
  id: number;
  title?: string;
  name?: string;
}

export interface ResourceMriCondition {
  id: number;
  condition_description?: string;
  name?: string;
}

export interface Resource {
  id: string;
  name: string;
  content: string;
  status: string;
  biomarkers?: Biomarker[];
  conditions?: Condition[];
  body_systems?: ResourceBodySystem[];
  mri_conditions?: ResourceMriCondition[];
  image?: string;
  video_url?: string;
}

export interface ListResourcesRequest {
  limit?: number;
  offset?: number;
  search?: string;
  biomarker_id?: number;
  condition_id?: number;
}

export interface ListResourcesResponse {
  resources: Resource[];
  total_count: number;
}

export interface ApiErrorResponse {
  description: string;
}

export const createResource = async (data: CreateResourceRequest): Promise<CreateResourceResponse> => {
  try {
    const response = await axiosInstance.post<CreateResourceResponse>('/api/v1/resources/', data);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to create resource');
  }
};

export const updateResource = async (data: UpdateResourceRequest): Promise<UpdateResourceResponse> => {
  try {
    const response = await axiosInstance.patch<UpdateResourceResponse>('/api/v1/resources/', data);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to update resource');
  }
};

export const deleteResource = async (data: DeleteResourceRequest): Promise<DeleteResourceResponse> => {
  try {
    const response = await axiosInstance.delete<DeleteResourceResponse>('/api/v1/resources/', {
      data
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to delete resource');
  }
};

export const listResources = async (params: ListResourcesRequest = {}): Promise<ListResourcesResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.biomarker_id !== undefined) queryParams.append('biomarker_id', params.biomarker_id.toString());
    if (params.condition_id !== undefined) queryParams.append('condition_id', params.condition_id.toString());

    const response = await axiosInstance.get<ListResourcesResponse>(`/api/v1/resources/list?${queryParams.toString()}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to fetch resources');
  }
};

export const getResourceById = async (id: string): Promise<Resource> => {
  try {
    const response = await axiosInstance.get<Resource>(`/api/v1/resources/${id}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to fetch resource');
  }
};

export interface GetResourceByIdRequest {
  resource_id: number;
}

export interface GetResourceByIdResponse {
  id: number;
  name: string;
  content: string;
  image: string;
  image_url: string;
  biomarkers: Biomarker[];
  conditions: Condition[];
  body_systems?: ResourceBodySystem[];
  mri_conditions?: ResourceMriCondition[];
  video_url?: string;
  created_at: string;
  updated_at: string;
  status: string;
}

export const getResourceByIdPost = async (data: GetResourceByIdRequest): Promise<GetResourceByIdResponse> => {
  try {
    const response = await axiosInstance.post<GetResourceByIdResponse>('/api/v1/resources/get/', data);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to fetch resource by ID');
  }
};

export const getResourceBodySystemLabel = (bodySystem: ResourceBodySystem): string =>
  bodySystem.title || bodySystem.name || `Body System ${bodySystem.id}`;

export const getResourceMriConditionLabel = (mriCondition: ResourceMriCondition): string =>
  mriCondition.condition_description || mriCondition.name || `MRI Condition ${mriCondition.id}`;

const resourceCenterApi = {
  createResource,
  updateResource,
  deleteResource,
  listResources,
  getResourceById,
  getResourceByIdPost
};

export default resourceCenterApi;
